import { Injectable, Inject, Optional, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import {
  PurchaseOrderItem,
  PurchaseOrderListResponse,
} from '../purchase-order.config';
import { PurchaseOrderControllerService } from '../../../../../api/purchase-service/api/purchaseOrderController.service';
import {
  PageInfoPurchaseOrderResultDTO,
  PurchaseOrderResultDTO,
} from '@app/api/purchase-service/model/models';
import { PurchaseOrderVendorConfirmedDTO } from '@app/api/purchase-service/model/purchaseOrderVendorConfirmedDTO';
import { PurchaseOrderDTO } from '@app/api/purchase-service/model/purchaseOrderDTO';
import { PurchaseOrderUpdateDTO } from '@app/api/purchase-service/model/purchaseOrderUpdateDTO';
import {
  WarehouseControllerService,
  WarehouseQueryReqDTO,
  WarehouseQueryResVO,
} from '@app/api/stock-service';
import { BASE_PATH as PURCHASE_BASE_PATH } from '@app/api/purchase-service/variables';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import { formatDateWithCurrentTime } from '../../procurement-date.util';

export interface SearchFormData {
  id?: number;
  purchaseRequest?: number;
  series?: string;
  vendorCode?: string;
}

export interface PurchaseOrderSearchParams {
  id?: number;
  purchaseRequest?: number;
  series?: string;
  vendorCode?: string;
  startDeliveryDate?: string;
  endDeliveryDate?: string;
  status?:
    | 'PENDING'
    | 'APPROVED'
    | 'REJECTED'
    | 'SENT'
    | 'VENDOR_CONFIRMED'
    | 'PARTIALLY_RECEIVED'
    | 'DELIVERED'
    | 'DONE'
    | 'CLOSED'
    | 'CANCELLED';
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

/** Receipt discrepancy row returned by GET /v1/purchase-order/{id}/discrepancies */
export interface PurchaseOrderDiscrepancy {
  id?: number;
  purchaseOrderId?: number;
  purchaseOrderCode?: string;
  purchaseOrderItemId?: number;
  skuCode?: string;
  warehouseWarrantId?: number;
  warehouseWarrantCode?: string;
  expectedQty?: number;
  receivedQty?: number;
  discrepancyQty?: number;
  discrepancyReason?: string;
  discrepancyNote?: string;
  status?: 'OPEN' | 'RESOLVED' | string;
  creationDate?: string;
  createdBy?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PurchaseOrderService {
  private purchaseOrderController: PurchaseOrderControllerService;
  private warehouseControllerService: WarehouseControllerService;
  private messageService: MessageService;
  private translateService: TranslateService;
  private httpClient = inject(HttpClient);
  private purchaseBasePath: string;

  constructor() {
    this.purchaseOrderController = inject(PurchaseOrderControllerService);
    this.warehouseControllerService = inject(WarehouseControllerService);
    this.messageService = inject(MessageService);
    this.translateService = inject(TranslateService);
    const injected = inject(PURCHASE_BASE_PATH, { optional: true });
    this.purchaseBasePath =
      (Array.isArray(injected) ? injected[0] : injected) || '/purchase';
  }

  // Get Purchase Orders list with pagination and search
  getPurchaseOrders(params: any): Observable<PurchaseOrderListResponse> {
    const {
      purchaseRequest,
      series,
      vendorCode,
      status,
      page = 0,
      size = 20,
      sortBy = 'creationDate',
      ascending = false,
    } = params;

    // Convert search parameters to API format
    // Normalize status to the API's union type
    const allowedStatuses = [
      'PENDING',
      'APPROVED',
      'REJECTED',
      'SENT',
      'VENDOR_CONFIRMED',
      'DELIVERED',
      'DONE',
      'CLOSED',
      'CANCELLED',
    ] as const;
    const normalizedStatus =
      status && (allowedStatuses as readonly string[]).includes(status)
        ? (status as PurchaseOrderSearchParams['status'])
        : undefined;

    const searchParams: PurchaseOrderSearchParams = {
      purchaseRequest: purchaseRequest ? Number(purchaseRequest) : undefined,
      series: series?.trim(),
      vendorCode: vendorCode?.trim(),
      status: normalizedStatus,
      page,
      size,
      sortBy,
      ascending,
    };

    // Remove undefined values
    Object.keys(searchParams).forEach((key) => {
      if (searchParams[key as keyof PurchaseOrderSearchParams] === undefined) {
        delete searchParams[key as keyof PurchaseOrderSearchParams];
      }
    });

    // Call the real API with deleteFlag = false
    return this.purchaseOrderController
      .page1(
        undefined,
        searchParams.purchaseRequest,
        searchParams.series,
        searchParams.vendorCode,
        undefined, // startPostingDate
        undefined, // endPostingDate
        searchParams.startDeliveryDate,
        searchParams.endDeliveryDate,
        searchParams.status as any, // generated client predates PARTIALLY_RECEIVED
        false, // deleteFlag = false
        searchParams.page,
        searchParams.size,
        searchParams.sortBy,
        searchParams.ascending,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any }
      )
      .pipe(
        map((response: PageInfoPurchaseOrderResultDTO) => {
          const mappedData = this.mapApiResponseToLocalData(response);
          return {
            data: mappedData.data || [],
            totalRecords: mappedData.totalRecords || 0,
            loading: false,
          };
        }),
        catchError((error) => {
          console.error('Error fetching Purchase Orders:', error);
          throw error;
        })
      );
  }

  // Get Purchase Order by ID
  getPurchaseOrderById(id: string): Observable<PurchaseOrderItem | null> {
    return this.purchaseOrderController
      .getDetail1(Number(id), 'body', false, {
        httpHeaderAccept: 'application/json' as any,
      })
      .pipe(
        map((response: any) => {
          if (response && response.data) {
            return this.mapApiItemToLocalItem(response.data);
          } else if (response) {
            return this.mapApiItemToLocalItem(response);
          }
          return null;
        }),
        catchError((error) => {
          console.error(
            '❌ Service: Error fetching Purchase Order by ID:',
            error
          );
          return of(null);
        })
      );
  }

  // Delete Purchase Order
  deletePurchaseOrder(id: string): Observable<string> {
    return this.purchaseOrderController._delete(Number(id)).pipe(
      map(() => 'Purchase Order deleted successfully'),
      catchError((error) => {
        console.error('Error deleting Purchase Order:', error);
        throw error;
      })
    );
  }

  // Create Purchase Order (with or without purchase request)
  createPurchaseOrder(data: PurchaseOrderDTO): Observable<any> {
    // Create payload with optional purchaseRequest field
    const purchaseOrderData = {
      purchaseRequest: data.purchaseRequest,
      series: data.series,
      vendorCode: data.vendorCode,
      deliveryDate: data.deliveryDate
        ? formatDateWithCurrentTime(data.deliveryDate)
        : data.deliveryDate,
      description: data.description,
      items: data.items,
    };

    return this.purchaseOrderController
      .addPurchaseOrder(purchaseOrderData as any)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error creating Purchase Order in normal mode:', error);
          return this.handleBlobError(error);
        })
      );
  }

  // Update Purchase Order
  updatePurchaseOrder(
    id: string,
    data: PurchaseOrderUpdateDTO
  ): Observable<any> {
    const request: PurchaseOrderUpdateDTO = {
      ...data,
      deliveryDate: data.deliveryDate
        ? formatDateWithCurrentTime(data.deliveryDate)
        : data.deliveryDate,
    };
    return this.purchaseOrderController
      .updatePurchaseOrder(Number(id), request)
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((error) => {
          console.error('❌ Service: Error updating purchase order:', error);
          return this.handleBlobError(error);
        })
      );
  }

  // Map API response to local data structure
  private mapApiResponseToLocalData(
    apiResponse: PageInfoPurchaseOrderResultDTO
  ): PurchaseOrderListResponse {
    // Filter out deleted items (deleteFlag = true)
    const filteredItems = (apiResponse.pageContent || []).filter(
      (item: PurchaseOrderResultDTO) => !item.deleteFlag
    );
    const mappedData = filteredItems.map((item: PurchaseOrderResultDTO) =>
      this.mapApiItemToLocalItem(item)
    );

    return {
      data: mappedData,
      totalRecords: apiResponse.totalElements || 0, // Use totalElements from API response
      loading: false,
    };
  }

  // Map API item to local item structure
  private mapApiItemToLocalItem(
    apiItem: PurchaseOrderResultDTO
  ): PurchaseOrderItem {
    // Calculate total from items if available
    let total = 0;
    let netTotal = 0;

    if (apiItem.items && apiItem.items.length > 0) {
      total = apiItem.items.reduce(
        (sum: number, item: any) => sum + (item.price || 0),
        0
      );
      netTotal = apiItem.items.reduce(
        (sum: number, item: any) =>
          sum + (item.price || 0) * (1 - (item.tax || 0)),
        0
      );
    }

    const mappedItem = {
      id: apiItem.id?.toString() || '',
      series: apiItem.series || '',
      purchaseRequest: apiItem.purchaseRequest || null,
      vendorCode: apiItem.vendorCode || '',
      vendor: apiItem.vendor || undefined, // Preserve vendor object from API
      description: apiItem.description || '',
      deliveryDate: apiItem.deliveryDate || '',
      creationDate: apiItem.creationDate || '',
      total: total,
      netTotal: netTotal,
      // Additional fields for compatibility
      supplier: apiItem.vendorCode || '',
      company: '', // Not available in API response
      status: apiItem.status || 'PENDING', // Use API status if available
      // Include items array for detail view
      items: apiItem.items || [],
    };

    return mappedItem;
  }

  // Update purchase order status
  updatePurchaseOrderStatus(
    id: number,
    status:
      | 'PENDING'
      | 'APPROVED'
      | 'REJECTED'
      | 'SENT'
      | 'VENDOR_CONFIRMED'
      | 'DELIVERED'
      | 'DONE'
      | 'CLOSED'
      | 'CANCELLED'
  ): Observable<PurchaseOrderResultDTO> {
    return this.purchaseOrderController
      .updatePurchaseOrderStatus(id, status)
      .pipe(
        map((response: PurchaseOrderResultDTO) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error updating purchase order status:', error);
          throw error;
        })
      );
  }

  /** Email PO to vendor then mark SENT (RBA-5913). */
  sendPurchaseOrderToVendor(id: number): Observable<PurchaseOrderResultDTO> {
    return this.purchaseOrderController.sendPurchaseOrderToVendor(id).pipe(
      map((response: PurchaseOrderResultDTO) => response),
      catchError((error) => {
        console.error('Error sending purchase order to vendor:', error);
        return this.handleBlobError(error);
      }),
    );
  }

  // Delete purchase order item
  deletePurchaseOrderItem(
    id: number,
    itemIds: number[]
  ): Observable<PurchaseOrderResultDTO> {
    return this.purchaseOrderController
      .deletePurchaseOrderItem(id, itemIds)
      .pipe(
        map((response: PurchaseOrderResultDTO) => {
          return response;
        }),
        catchError((error) => {
          console.error('Error deleting purchase order item:', error);
          return this.handleBlobError(error);
        })
      );
  }

  // Load warehouses for dropdown options
  loadWarehouses(): Observable<Array<{ label: string; value: string }>> {
    const queryDTO: WarehouseQueryReqDTO = {
      page: 0,
      size: 1000,
      status: true, // Only active warehouses
    };

    return this.warehouseControllerService.queryWarehousePage(queryDTO).pipe(
      switchMap((response: any) => {
        if (BlobConverterUtil.isBlob(response)) {
          return from(BlobConverterUtil.convertToJson(response));
        }
        return of(response);
      }),
      map((response: any) => {
        const warehouses: WarehouseQueryResVO[] = response.pageContent || [];
        return warehouses
          .filter(
            (warehouse: WarehouseQueryResVO) =>
              warehouse && warehouse.code && warehouse.name
          )
          .map((warehouse: WarehouseQueryResVO) => ({
            label: `${warehouse.code} - ${warehouse.name}`,
            value: warehouse.code || '',
          }));
      }),
      catchError((error) => {
        console.error('Error loading warehouses:', error);
        return of([]);
      })
    );
  }

  /**
   * Subscribe to Observable with automatic error handling
   * @param observable - The Observable to subscribe to
   * @param onNext - Callback for successful response
   * @param errorKey - i18n key for error message (optional)
   * @returns Subscription (can be used with takeUntil)
   */
  subscribeWithErrorHandling<T>(
    observable: Observable<T>,
    onNext: (value: T) => void,
    errorKey?: string
  ) {
    return observable.subscribe({
      next: onNext,
      error: (error) => {
        console.error('Error:', error);
        if (errorKey) {
          this.showErrorMessage(errorKey);
        }
      },
    });
  }

  /**
   * Show error message using MessageService
   * @param detailKey - i18n key for error detail message
   */
  showErrorMessage(detailKey: string): void {
    this.messageService.add({
      severity: 'error',
      summary: String(this.translateService.instant('common.messages.error')),
      detail: this.translateService.instant(detailKey),
    });
  }

  /**
   * Show success message using MessageService
   * @param detailKey - i18n key for success detail message
   */
  showSuccessMessage(detailKey: string): void {
    this.messageService.add({
      severity: 'success',
      summary: String(
        this.translateService.instant('common.messages.success')
      ),
      detail: this.translateService.instant(detailKey),
    });
  }

  // Handle Blob error responses by converting to text
  private handleBlobError(error: any): Observable<never> {
    // Check if error.error is a Blob
    if (error?.error instanceof Blob) {
      return from(error.error.text() as Promise<string>).pipe(
        switchMap((text: string) => {
          // Try to parse as JSON
          try {
            const parsedError = JSON.parse(text);
            // Create a new error with the parsed error as the error property
            const newError = {
              ...error,
              error: parsedError,
            };
            return throwError(() => newError);
          } catch {
            // If parsing fails, use the text as string
            const newError = {
              ...error,
              error: text,
            };
            return throwError(() => newError);
          }
        })
      );
    }
    // If not a Blob, throw the original error
    return throwError(() => error);
  }

  confirmPurchaseOrderByVendor(
    id: number,
    payload: PurchaseOrderVendorConfirmedDTO
  ): Observable<PurchaseOrderResultDTO> {
    return this.purchaseOrderController.confirmPurchaseOrderByVendor(
      id,
      payload,
      'body',
      false,
      { httpHeaderAccept: 'application/json' as any }
    );
  }

  /** Receipt discrepancies reported by the warehouse for this PO (US-B3-04). */
  getDiscrepancies(id: number): Observable<PurchaseOrderDiscrepancy[]> {
    return this.httpClient
      .get<PurchaseOrderDiscrepancy[]>(
        `${this.purchaseBasePath}/v1/purchase-order/${id}/discrepancies`,
        { headers: { Accept: 'application/json' } }
      )
      .pipe(
        catchError((error) => {
          console.error('Error fetching purchase order discrepancies:', error);
          return of([]);
        })
      );
  }

  /** Purchasing decision: accept received quantities and close the order short. */
  closeShort(id: number, reason: string): Observable<PurchaseOrderResultDTO> {
    return this.httpClient
      .post<PurchaseOrderResultDTO>(
        `${this.purchaseBasePath}/v1/purchase-order/${id}/close-short`,
        { reason },
        { headers: { 'Content-Type': 'application/json' } }
      )
      .pipe(
        catchError((error) => {
          console.error('Error closing purchase order short:', error);
          return this.handleBlobError(error);
        })
      );
  }
}
